const HTTP_403_MESSAGE = 'Request failed with status code 403';

const UNAUTHORIZED_PATTERN = /status code 403|Unauthorized|NotAuthorized/i;

const statusOf = err => err?.$metadata?.httpStatusCode ?? err?.response?.statusCode ?? err?.response?.status;

const isUnauthorized = err => {
    if (statusOf(err) === 403) {
        return true;
    }
    if (err?.name === 'UnauthorizedException' || err?.name === 'NotAuthorizedException') {
        return true;
    }
    return typeof err?.message === 'string' && UNAUTHORIZED_PATTERN.test(err.message);
};

const mapTransportMessage = err => {
    if (isUnauthorized(err)) {
        return HTTP_403_MESSAGE;
    }
    return err?.message || String(err);
};

export const tryParseJSON = value => {
    if (typeof value !== 'string') {
        return value;
    }
    try {
        return JSON.parse(value);
    } catch (e) {
        return value;
    }
};

/**
 * `ServiceFactory.BaseAPI.graphql` dựa vào shape lỗi của v2: LUÔN có `errors[0].message`,
 * và dùng `data` để phân biệt partial response.
 *
 * Amplify v6 ném hai dạng khác nhau:
 *  - Lỗi GraphQL  -> { data, errors: [...] }  — tương thích v2, giữ nguyên.
 *  - Lỗi transport/auth (ApiError, NoValidAuthTokens, NetworkError) -> KHÔNG có `.errors`.
 *    Khi đó `err?.errors[0]?.message` ở ServiceFactory.js:64 sẽ ném TypeError (optional
 *    chaining bảo vệ `err` nhưng không bảo vệ index [0]) và làm sập handler.
 *
 * Hàm này bảo đảm shape v2 trong mọi trường hợp.
 */
export const normalizeGraphqlError = err => {
    if (Array.isArray(err?.errors)) {
        return err;
    }

    // Phải trả về một Error THẬT, không phải plain object: ở v2 lỗi transport là Error của axios
    // (có message + stack). Nếu reject bằng plain object thì dev overlay của webpack-dev-server chỉ
    // in "Unknown promise rejection reason" và mất sạch thông tin chẩn đoán.
    const message = mapTransportMessage(err);
    const normalized = err instanceof Error ? err : new Error(message);
    normalized.message = message;
    normalized.data = err?.data;
    normalized.errors = [{ message }];
    normalized.originalError = err;
    return normalized;
};

/**
 * v2 (axios) ném error có `err.response.data` (đã parse) + `err.response.status`.
 * v6 ném `ApiError` có `err.response.body` (string) + `err.response.statusCode`.
 * Dựng lại shape v2 để consumer cũ đọc được, giữ nguyên các field của v6.
 */
export const normalizeRestError = async err => {
    const response = err?.response;
    if (!response) {
        return err;
    }

    let rawBody = response.body;
    if (rawBody && typeof rawBody.text === 'function') {
        try {
            rawBody = await rawBody.text();
        } catch (e) {
            rawBody = undefined;
        }
    }

    err.response = {
        ...response,
        body: rawBody,
        data: tryParseJSON(rawBody),
        status: response.statusCode ?? response.status,
        statusCode: response.statusCode ?? response.status
    };
    return err;
};

export default { normalizeGraphqlError, normalizeRestError, tryParseJSON, HTTP_403_MESSAGE };
